import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, EmptyState, Field, DemoBadge, BookCover, SectionTitle, Tag } from "@/components/lf";
import { useStore, fmtDate } from "@/lib/hooks";
import { add, write, StorageError } from "@/lib/storage";
import { getBook, rupiah } from "@/lib/catalog";
import { setQty, removeFromCart, SHIPPING } from "@/lib/cart";

export default function Keranjang() {
  const cart = useStore("cart");
  const orders = useStore("orders");
  const [ship, setShip] = useState("reguler");
  const [info, setInfo] = useState({ name: "", phone: "", address: "", note: "" });
  const [placed, setPlaced] = useState(null);

  const lines = cart.map((c) => ({ ...c, book: getBook(c.bookId) })).filter((l) => l.book);
  const subtotal = lines.reduce((a, l) => a + l.book.price * l.qty, 0);
  const shipOpt = SHIPPING.find((s) => s.id === ship);
  const total = subtotal + (lines.length ? shipOpt.fee : 0);

  const checkout = (e) => {
    e.preventDefault();
    if (!info.name.trim() || !info.phone.trim()) return toast.error("Nama dan nomor telepon wajib diisi.");
    if (ship === "reguler" && !info.address.trim()) return toast.error("Alamat wajib diisi untuk pengiriman.");
    try {
      const order = add("orders", {
        number: `DEMO-${Date.now().toString().slice(-6)}`, demo: true, status: "Pesanan demo dibuat",
        items: lines.map((l) => ({ bookId: l.bookId, title: l.book.title, price: l.book.price, qty: l.qty })),
        subtotal, shipping: { id: ship, label: shipOpt.label, fee: shipOpt.fee }, total, customer: info,
      });
      write("cart", []);
      setPlaced(order);
      toast.success("Pesanan demo dibuat.");
    } catch (err) { toast.error(err instanceof StorageError ? err.message : "Gagal membuat pesanan."); }
  };

  return (
    <div>
      <PageHeader testid="page-title-keranjang" overline="Toko fisik" title="Keranjang / Checkout" subtitle="Seluruh proses checkout di sini adalah demonstrasi. Tidak ada pembayaran, pengiriman, atau tagihan nyata.">
        <DemoBadge />
      </PageHeader>

      {placed && (
        <div data-testid="order-success" className="border border-[#545B6B] bg-[#151518] rounded-md p-6 mb-8 flex gap-4">
          <CheckCircle2 className="text-[#E6E2D6] shrink-0" />
          <div><div className="font-serif text-2xl">Pesanan demo {placed.number} dibuat</div><p className="text-sm text-[#929197] mt-1">Total {rupiah(placed.total)} — hanya simulasi, tidak ada uang yang diproses. Lihat riwayat di bawah.</p></div>
        </div>
      )}

      {lines.length === 0 ? (
        <EmptyState testid="empty-cart" title="Keranjang masih kosong" text="Jelajahi Toko Buku dan tambahkan buku demonstrasi."><Link to="/toko-buku" className="lf-btn" data-testid="cart-go-store">Ke Toko Buku</Link></EmptyState>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          <div className="xl:col-span-3 space-y-3" data-testid="cart-lines">
            {lines.map((l) => (
              <div key={l.bookId} data-testid="cart-line" className="lf-card p-4 flex gap-4 items-center">
                <div className="w-16 shrink-0"><BookCover book={l.book} size="sm" /></div>
                <div className="flex-1 min-w-0"><div className="font-serif text-xl leading-tight truncate">{l.book.title}</div><div className="text-xs text-[#929197]">{l.book.author} · {rupiah(l.book.price)} (demo)</div></div>
                <div className="flex items-center border border-[#29292D] rounded-sm">
                  <button aria-label="Kurangi" data-testid="cart-qty-minus" onClick={() => setQty(l.bookId, l.qty - 1)} className="p-2 hover:bg-[#1B1B1F]"><Minus size={13} /></button>
                  <span data-testid="cart-qty" className="w-8 text-center text-sm">{l.qty}</span>
                  <button aria-label="Tambah" data-testid="cart-qty-plus" onClick={() => setQty(l.bookId, l.qty + 1)} className="p-2 hover:bg-[#1B1B1F]"><Plus size={13} /></button>
                </div>
                <div className="w-28 text-right font-serif text-xl hidden sm:block">{rupiah(l.book.price * l.qty)}</div>
                <button aria-label="Hapus" data-testid="cart-remove" onClick={() => removeFromCart(l.bookId)} className="p-2 text-[#929197] hover:text-[#d98a8a]"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          <form onSubmit={checkout} className="xl:col-span-2 lf-card p-6 space-y-4 h-fit" data-testid="checkout-form">
            <h2 className="font-serif text-2xl">Data checkout (demo)</h2>
            <Field label="Nama lengkap"><input data-testid="checkout-name-input" className="lf-field" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} /></Field>
            <Field label="Telepon"><input data-testid="checkout-phone-input" className="lf-field" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></Field>
            <Field label="Metode pengiriman (opsional)"><select data-testid="checkout-shipping-select" className="lf-field" value={ship} onChange={(e) => setShip(e.target.value)}>{SHIPPING.map((s) => <option key={s.id} value={s.id}>{s.label} — {s.fee ? rupiah(s.fee) : "Gratis"}</option>)}</select></Field>
            {ship === "reguler" && <Field label="Alamat"><textarea data-testid="checkout-address-input" rows={2} className="lf-field" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} /></Field>}
            <div className="border-t border-[#29292D] pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#929197]">Subtotal</span><span data-testid="cart-subtotal">{rupiah(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#929197]">Pengiriman</span><span data-testid="cart-shipping">{shipOpt.fee ? rupiah(shipOpt.fee) : "Gratis"}</span></div>
              <div className="flex justify-between font-serif text-2xl pt-2"><span>Total</span><span data-testid="cart-total">{rupiah(total)}</span></div>
            </div>
            <button data-testid="checkout-submit-button" className="lf-btn w-full !py-3">Buat pesanan demo</button>
            <p className="text-xs text-[#d4a95f]">Demonstrasi: tidak ada gerbang pembayaran dan tidak ada penagihan.</p>
          </form>
        </div>
      )}

      <section className="mt-14">
        <SectionTitle>Riwayat pesanan</SectionTitle>
        {orders.length === 0 ? <EmptyState testid="empty-orders" title="Belum ada pesanan demo" text="Pesanan demo yang Anda buat akan muncul di sini." /> : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map((o) => (
              <Card key={o.id} data-testid="order-item">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><div className="font-serif text-xl">{o.number}</div><div className="text-xs text-[#929197]">{fmtDate(o.createdAt)} · {o.items.reduce((a, i) => a + i.qty, 0)} item</div></div>
                  <div className="flex items-center gap-3"><Tag tone="warn">DEMO</Tag><span className="font-serif text-xl">{rupiah(o.total)}</span></div>
                </div>
                <ul className="text-sm text-[#929197] mt-3 space-y-0.5">{o.items.map((i) => <li key={i.bookId}>{i.qty}× {i.title}</li>)}</ul>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
